// Offline RFP analysis engine — pure logic, no AI.

export type Category = "Technical" | "Commercial" | "Security" | "Past Performance" | "Administrative";
export type Priority = "Critical" | "High" | "Medium";
export type ComplianceStatus = "Not Started" | "In Progress" | "Compliant" | "Partial" | "Non-Compliant";

export interface Requirement {
  id: string;
  text: string;
  category: Category;
  priority: Priority;
  responseLocation: string;
  status: ComplianceStatus;
  keyword: string;
}

const KEYWORDS = /\b(must|shall|required|mandatory|will\s+provide|is\s+required\s+to|are\s+required\s+to)\b/i;

const CATEGORY_RULES: { cat: Category; patterns: RegExp[] }[] = [
  { cat: "Security", patterns: [/\b(security|encrypt|compliance|fedramp|soc\s?2|iso\s?27001|hipaa|gdpr|audit|vulnerab|penetration|access\s+control|authentication|authoriz)/i] },
  { cat: "Commercial", patterns: [/\b(price|pricing|cost|fee|payment|invoice|warranty|sla|service\s+level|penalty|discount|budget|license)/i] },
  { cat: "Past Performance", patterns: [/\b(past\s+performance|reference|case\s+study|prior\s+experience|similar\s+project|portfolio|track\s+record)/i] },
  { cat: "Administrative", patterns: [/\b(submission|deadline|format|page\s+limit|font|cover\s+letter|signature|naics|cage|duns|uei|insurance|bond)/i] },
  { cat: "Technical", patterns: [/\b(system|integration|api|platform|architecture|database|cloud|infrastructure|performance|scalability|interface|functional|technical)/i] },
];

function categorize(text: string): Category {
  for (const rule of CATEGORY_RULES) {
    if (rule.patterns.some((p) => p.test(text))) return rule.cat;
  }
  return "Technical";
}

function prioritize(text: string, keyword: string): Priority {
  const k = keyword.toLowerCase();
  if (/mandatory|shall|must/.test(k) || /\b(critical|essential)\b/i.test(text)) return "Critical";
  if (/required/.test(k)) return "High";
  return "Medium";
}

export function extractRequirements(rfpText: string): Requirement[] {
  if (!rfpText.trim()) return [];
  // Split into sentences. Keep it simple — punctuation followed by space + capital, or bullets / numbered lines.
  const lines = rfpText
    .replace(/\r/g, "")
    .split(/(?<=[.!?])\s+(?=[A-Z0-9])|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const reqs: Requirement[] = [];
  let counter = 1;
  for (const line of lines) {
    const m = line.match(KEYWORDS);
    if (!m) continue;
    if (line.length < 12 || line.length > 600) continue;
    const cleaned = line.replace(/^\s*[-•*\d.)\]]+\s*/, "").trim();
    reqs.push({
      id: `R-${String(counter).padStart(3, "0")}`,
      text: cleaned,
      category: categorize(cleaned),
      priority: prioritize(cleaned, m[0]),
      responseLocation: "",
      status: "Not Started",
      keyword: m[0].toLowerCase(),
    });
    counter++;
  }
  return reqs;
}

export function toCSV(reqs: Requirement[]): string {
  const esc = (s: string) => `"${(s ?? "").replace(/"/g, '""')}"`;
  const header = ["ID", "Requirement", "Category", "Priority", "Response Location", "Compliance Status"].join(",");
  const rows = reqs.map((r) =>
    [r.id, r.text, r.category, r.priority, r.responseLocation, r.status].map(esc).join(","),
  );
  return [header, ...rows].join("\n");
}

// ---------- Scoring engine ----------

export interface Criterion {
  name: string;
  weight: number; // 0..100
  keywords: string[];
}

export const DEFAULT_CRITERIA: Criterion[] = [
  { name: "Functional",     weight: 30, keywords: ["feature", "functional", "capability", "workflow", "user", "module"] },
  { name: "Implementation", weight: 20, keywords: ["implementation", "timeline", "milestone", "deploy", "rollout", "training", "onboarding"] },
  { name: "Security",       weight: 20, keywords: ["security", "encryption", "compliance", "audit", "access control", "soc 2", "iso", "gdpr"] },
  { name: "Commercial",     weight: 20, keywords: ["price", "cost", "license", "fee", "warranty", "sla", "support"] },
  { name: "References",     weight: 10, keywords: ["reference", "case study", "past performance", "client", "portfolio"] },
];

// Try to parse weights out of a pasted evaluation criteria block, e.g. "Functional - 30%"
export function parseCriteriaWeights(text: string, base = DEFAULT_CRITERIA): Criterion[] {
  if (!text.trim()) return base;
  return base.map((c) => {
    const re = new RegExp(`${c.name}[^\\d%]{0,40}(\\d{1,3})\\s*%`, "i");
    const m = text.match(re);
    return m ? { ...c, weight: Math.min(100, parseInt(m[1], 10)) } : c;
  });
}

export interface ScoreResult {
  criterion: string;
  weight: number;
  coverage: number;   // 0..100
  hits: number;
  weighted: number;
  redFlag: boolean;
}

export function scoreResponse(response: string, criteria: Criterion[]): ScoreResult[] {
  const lower = response.toLowerCase();
  const wordCount = Math.max(1, lower.split(/\s+/).length);
  return criteria.map((c) => {
    let hits = 0;
    for (const kw of c.keywords) {
      const matches = lower.match(new RegExp(`\\b${kw.replace(/\s+/g, "\\s+")}\\b`, "g"));
      if (matches) hits += matches.length;
    }
    // Coverage: hits relative to expected density (1 hit per ~150 words is "full").
    const expected = Math.max(1, wordCount / 150);
    const coverage = Math.min(100, Math.round((hits / expected) * 50));
    const weighted = Math.round((coverage * c.weight) / 100);
    return {
      criterion: c.name,
      weight: c.weight,
      coverage,
      hits,
      weighted,
      redFlag: c.weight >= 20 && coverage < 40,
    };
  });
}
